import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { Tendv2ContentItem, tendApi, OverlayTemplateConfig, OverlayTemplateResponse } from '../../api/tendApi';
import { Logo } from '../../api/logoApi';
import TemplateCard from './TemplateCard';
import OverlayPreview from './OverlayPreview';
import { BrandPaletteProvider } from '../../contexts/BrandPaletteContext';

interface TemplateManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentItem: Tendv2ContentItem;
  brandId: number;
  brandName: string;
  brandDefaultLogo: Logo | null;
  brandLogoDisplayMode: string;
  currentTemplateId: number | null;
  defaultTemplateId: number | null;
  isSuperAdmin: boolean;
  onSelectTemplate: (templateId: number, config: OverlayTemplateConfig) => void;
  onSetDefault: (templateId: number) => void;
  categoryBorderRadius?: number;
  categoryBgColor?: string;
  // Current overlay config from the content card (user's unsaved changes)
  currentOverlayConfig?: OverlayTemplateConfig;
  // Current edited title/subheader/body (respects caption mode)
  currentTitle?: string;
  currentSubheader?: string;
  currentBody?: string;
}

// Default overlay config for new templates
const DEFAULT_OVERLAY_CONFIG: OverlayTemplateConfig = {
  showOverlayLogo: true,
  showOverlayTitle: true,
  showOverlaySubheader: false,
  showOverlayBody: false,
  showOverlayCategory: false,
  showOverlayGradient: true,
  logoPosition: 'bottom-center',
  titlePosition: 'bottom-center',
  subheaderPosition: 'bottom-center',
  bodyPosition: 'bottom-center',
  categoryPosition: 'top-left',
  titleFontSize: 16,
  subheaderFontSize: 12,
  bodyFontSize: 12,
  logoScale: 1.0,
  categoryBgColor: '#2563eb',
  gradientDirection: 'up',
  gradientColor: '#000000',
  gradientOpacity: 70,
  titleBgEnabled: false,
  titleBgColor: '#000000',
  titleBgOpacity: 80,
  titleTextColor: '#ffffff',
  subheaderBgEnabled: false,
  subheaderBgColor: '#000000',
  subheaderBgOpacity: 80,
  subheaderTextColor: '#ffffff',
  bodyBgEnabled: false,
  bodyBgColor: '#000000',
  bodyBgOpacity: 80,
  bodyTextColor: '#ffffff',
  borderEnabled: false,
  borderColor: '#ffffff',
  borderThickness: 4,
};

type TabType = 'all' | 'global' | 'brand';

interface LocalTemplateState {
  config: OverlayTemplateConfig;
  name: string;
  description: string;
  isDirty: boolean;
}

const TemplateManagementModal: React.FC<TemplateManagementModalProps> = ({
  isOpen,
  onClose,
  contentItem,
  brandId,
  brandName,
  brandDefaultLogo,
  brandLogoDisplayMode,
  currentTemplateId,
  defaultTemplateId,
  isSuperAdmin,
  onSelectTemplate,
  onSetDefault,
  categoryBorderRadius = 50,
  categoryBgColor = '#2563eb',
  currentOverlayConfig,
  currentTitle,
  currentSubheader,
  currentBody,
}) => {
  const [templates, setTemplates] = useState<OverlayTemplateResponse[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>(isSuperAdmin ? 'all' : 'all');

  // Special ID for the "Current Settings" pseudo-template
  const CURRENT_CONFIG_ID = -1;
  const [isLoading, setIsLoading] = useState(true);

  // initialTemplateId: Set ONCE on open - determines first slot position (never changes)
  // selectedTemplateId: Tracks current selection - changes when user clicks (controls SELECTED badge)
  const [initialTemplateId] = useState<number | null>(currentTemplateId ?? CURRENT_CONFIG_ID);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    currentTemplateId ?? CURRENT_CONFIG_ID
  );
  const [isCreating, setIsCreating] = useState(false);

  // Local config state for the "Current Settings" card (allows editing without affecting saved templates)
  const [currentConfigState, setCurrentConfigState] = useState<OverlayTemplateConfig>(
    currentOverlayConfig || DEFAULT_OVERLAY_CONFIG
  );

  // Hover state for the Current Settings card
  const [isCurrentCardHovered, setIsCurrentCardHovered] = useState(false);

  // Local state for each template's edits
  const [localStates, setLocalStates] = useState<Record<number, LocalTemplateState>>({});

  // Ref for scrollable container to preserve scroll position on delete
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Load templates
  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const scope = activeTab === 'all' ? undefined : activeTab;
      const result = await tendApi.getOverlayTemplatesForModal(brandId, { scope });
      setTemplates(result);

      // Initialize local states for loaded templates
      const newLocalStates: Record<number, LocalTemplateState> = {};
      result.forEach((t) => {
        newLocalStates[t.id] = {
          config: t.config,
          name: t.name || '',
          description: t.description || '',
          isDirty: false,
        };
      });
      setLocalStates(newLocalStates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setIsLoading(false);
    }
  }, [brandId, activeTab]);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, loadTemplates]);

  // Filter templates by tab
  const filteredTemplates = useMemo(() => {
    let filtered: OverlayTemplateResponse[];
    if (activeTab === 'global') {
      filtered = templates.filter(t => t.is_global);
    } else if (activeTab === 'brand') {
      filtered = templates.filter(t => !t.is_global);
    } else {
      filtered = templates;
    }
    // Sort by display_order
    return filtered.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
  }, [templates, activeTab]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before starting drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder templates and save to API
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredTemplates.findIndex(t => t.id === active.id);
      const newIndex = filteredTemplates.findIndex(t => t.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Reorder locally first for immediate feedback
        const reordered = arrayMove(filteredTemplates, oldIndex, newIndex);

        // Update display_order for affected templates
        const updates = reordered.map((t, index) => ({
          id: t.id,
          display_order: index,
        }));

        // Update local state
        setTemplates(prev => {
          const newTemplates = [...prev];
          updates.forEach(update => {
            const idx = newTemplates.findIndex(t => t.id === update.id);
            if (idx !== -1) {
              newTemplates[idx] = { ...newTemplates[idx], display_order: update.display_order };
            }
          });
          // Sort by display_order
          return newTemplates.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
        });

        // Save to backend
        try {
          await tendApi.updateTemplateOrder(updates);
        } catch (error) {
          console.error('Failed to save template order:', error);
          // Reload on error to get correct order
          loadTemplates();
        }
      }
    }
  }, [filteredTemplates, loadTemplates]);

  // Handle config change for a template
  const handleConfigChange = useCallback((templateId: number, updates: Partial<OverlayTemplateConfig>) => {
    setLocalStates(prev => {
      const current = prev[templateId];
      if (!current) return prev;

      return {
        ...prev,
        [templateId]: {
          ...current,
          config: { ...current.config, ...updates },
          isDirty: true,
        },
      };
    });
  }, []);

  // Handle name change
  const handleNameChange = useCallback((templateId: number, name: string) => {
    setLocalStates(prev => {
      const current = prev[templateId];
      if (!current) return prev;

      return {
        ...prev,
        [templateId]: {
          ...current,
          name,
          isDirty: true,
        },
      };
    });
  }, []);

  // Handle description change
  const handleDescriptionChange = useCallback((templateId: number, description: string) => {
    setLocalStates(prev => {
      const current = prev[templateId];
      if (!current) return prev;

      return {
        ...prev,
        [templateId]: {
          ...current,
          description,
          isDirty: true,
        },
      };
    });
  }, []);

  // Save template
  const handleSave = useCallback(async (templateId: number) => {
    const localState = localStates[templateId];
    const template = templates.find(t => t.id === templateId);
    if (!localState || !template) return;

    // For global templates edited by non-SuperAdmin, create a copy
    if (template.is_global && !isSuperAdmin) {
      const copied = await tendApi.copyOverlayTemplate(templateId, brandId, {
        name: localState.name,
        description: localState.description,
      });

      if (copied) {
        // Update the copy with the config
        await tendApi.updateOverlayTemplate(copied.id, localState.config);
        await loadTemplates();
        setSelectedTemplateId(copied.id);
        // The user just edited a global template into a brand-owned copy.
        // Re-apply so the active post/carousel switches to the copy and
        // picks up the latest config (don't make them refresh).
        onSelectTemplate(copied.id, localState.config);
      }
    } else {
      // Direct update
      await tendApi.updateOverlayTemplate(templateId, localState.config, {
        name: localState.name,
        description: localState.description,
      });
      await loadTemplates();
      // If the saved template is the one currently applied to this post/
      // carousel, re-apply it so changes (e.g. toggling a section off) flow
      // into local state and overwrite the frozen overlay_config on the row.
      if (templateId === currentTemplateId) {
        onSelectTemplate(templateId, localState.config);
      }
    }
  }, [localStates, templates, isSuperAdmin, brandId, loadTemplates, onSelectTemplate, currentTemplateId]);

  // Revert template changes
  const handleRevert = useCallback((templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    setLocalStates(prev => ({
      ...prev,
      [templateId]: {
        config: template.config,
        name: template.name || '',
        description: template.description || '',
        isDirty: false,
      },
    }));
  }, [templates]);

  // Select template - also persist to backend to clear overlay_config
  const handleSelect = useCallback(async (templateId: number) => {
    setSelectedTemplateId(templateId);
    const localState = localStates[templateId];
    if (localState) {
      // Persist to backend: set overlay_template_id and clear overlay_config
      // The backend enforces mutual exclusivity but we explicitly pass null for clarity
      await tendApi.updateTendV2Content(contentItem.id, {
        overlay_template_id: templateId,
        overlay_config: null,  // Clear custom config when selecting a saved template
      });
      onSelectTemplate(templateId, localState.config);
    }
  }, [localStates, onSelectTemplate, contentItem.id]);

  // Make default - works for both global templates (SuperAdmin) and brand templates
  const handleMakeDefault = useCallback(async (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    // Use the new setTemplateAsDefault API which handles both global and brand defaults
    const result = await tendApi.setTemplateAsDefault(templateId);
    if (result) {
      // Also update BrandOverlaySettings for brand templates (backward compatibility)
      if (!template.is_global) {
        await tendApi.updateBrandOverlaySettings(brandId, {
          default_overlay_template_id: templateId,
        });
      }
      // Reload templates to get updated is_default values
      await loadTemplates();
      onSetDefault(templateId);
    }
  }, [templates, brandId, loadTemplates, onSetDefault]);

  // Copy template (for global → brand)
  const handleCopy = useCallback(async (templateId: number) => {
    const copied = await tendApi.copyOverlayTemplate(templateId, brandId);
    if (copied) {
      await loadTemplates();
      setSelectedTemplateId(copied.id);
    }
  }, [brandId, loadTemplates]);

  // Delete template
  const handleDelete = useCallback(async (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    // Preserve scroll position before delete
    const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;

    if (template.is_global && isSuperAdmin) {
      // Hard delete for global templates (SuperAdmin only)
      if (window.confirm('Are you sure you want to permanently delete this global template? This affects all brands.')) {
        await tendApi.hardDeleteOverlayTemplate(templateId);
        await loadTemplates();
        // Restore scroll position after re-render
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollTop;
          }
        });
      }
    } else if (!template.is_global) {
      // Soft delete for brand templates
      await tendApi.hideOverlayTemplate(templateId);
      await loadTemplates();
      // Restore scroll position after re-render
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollTop;
        }
      });
    }
  }, [templates, isSuperAdmin, loadTemplates]);

  // Get effective default template config (brand default → global default → DEFAULT_OVERLAY_CONFIG)
  const effectiveDefaultConfig = useMemo(() => {
    // First try brand default
    if (defaultTemplateId) {
      const brandDefault = templates.find(t => t.id === defaultTemplateId);
      if (brandDefault) return brandDefault.config;
    }
    // Then try first global template as global default
    const globalDefault = templates.find(t => t.is_global);
    if (globalDefault) return globalDefault.config;
    // Fall back to hardcoded default
    return DEFAULT_OVERLAY_CONFIG;
  }, [templates, defaultTemplateId]);

  // Create new template
  const handleCreateNew = useCallback(async () => {
    setIsCreating(true);
    // Preserve scroll position
    const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;

    try {
      // SuperAdmin on Global tab creates global templates (brandId: undefined/null)
      // Otherwise create brand template
      const isGlobalCreate = isSuperAdmin && activeTab === 'global';

      const newTemplate = await tendApi.createOverlayTemplateV2(effectiveDefaultConfig, {
        brandId: isGlobalCreate ? undefined : brandId,
        name: isGlobalCreate ? 'New Global Template' : 'New Template',
        description: '',
      });

      if (newTemplate) {
        await loadTemplates();
        setSelectedTemplateId(newTemplate.id);
        // Restore scroll position after re-render
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollTop;
          }
        });
      }
    } catch (error) {
      console.error('Failed to create template:', error);
    } finally {
      setIsCreating(false);
    }
  }, [brandId, loadTemplates, isSuperAdmin, activeTab, effectiveDefaultConfig]);

  // Close handler - warn about unsaved changes
  const handleClose = useCallback(() => {
    const hasUnsaved = Object.values(localStates).some(s => s.isDirty);
    if (hasUnsaved) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  }, [localStates, onClose]);

  if (!isOpen) return null;

  // Portaled to body like every other modal (TendModal, TacticEditModal,
  // DraggablePreviewPanel): the card that mounts this is an isolated
  // stacking context (root `isolate`, for the feedback panel z-tuck), so
  // an inline fixed overlay would be trapped inside it and sibling cards
  // would paint over the modal.
  return createPortal(
    <BrandPaletteProvider brandId={brandId}>
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      // Body-level siblings compete at page level: the tended item modal
      // (ContentCard / TendButton) sits at 999999, so this veil takes the
      // editor-over-tended-item tier TacticEditModal uses. The old z-[10000]
      // only worked while the modal rendered inline inside that modal.
      style={{ zIndex: Z.EDITOR_MODAL }}
      onClick={handleClose}
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl w-[calc(100vw-80px)] max-w-[1600px] max-h-[90vh] overflow-hidden flex flex-col mx-10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Overlay Templates</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage overlay templates for {brandName}
            </p>
          </div>

          {/* Tabs (SuperAdmin only) */}
          {isSuperAdmin && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {(['all', 'global', 'brand'] as TabType[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors
                    ${activeTab === tab
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          )}

          {/* Close button */}
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Template grid */}
        <div ref={scrollContainerRef} className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredTemplates.map(t => t.id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
              {/* FIRST SLOT: Based on initialTemplateId (what was selected when modal opened) */}
              {initialTemplateId && initialTemplateId !== CURRENT_CONFIG_ID ? (
                // A saved template was initially selected - show it first
                (() => {
                  const initialTemplate = filteredTemplates.find(t => t.id === initialTemplateId);
                  const localState = initialTemplate ? localStates[initialTemplate.id] : null;
                  if (!initialTemplate || !localState) return null;

                  return (
                    <TemplateCard
                      key={`first-${initialTemplate.id}`}
                      template={initialTemplate}
                      contentItem={contentItem}
                      brandName={brandName}
                      brandLogo={brandDefaultLogo}
                      brandId={brandId}
                      logoDisplayMode={brandLogoDisplayMode}
                      isSelected={selectedTemplateId === initialTemplate.id}
                      isDefault={initialTemplate.is_default ?? false}
                      isSuperAdmin={isSuperAdmin}
                      localConfig={localState.config}
                      localName={localState.name}
                      localDescription={localState.description}
                      isDirty={localState.isDirty}
                      onConfigChange={(updates) => handleConfigChange(initialTemplate.id, updates)}
                      onNameChange={(name) => handleNameChange(initialTemplate.id, name)}
                      onDescriptionChange={(desc) => handleDescriptionChange(initialTemplate.id, desc)}
                      onSelect={() => handleSelect(initialTemplate.id)}
                      onMakeDefault={() => handleMakeDefault(initialTemplate.id)}
                      onSave={() => handleSave(initialTemplate.id)}
                      onRevert={() => handleRevert(initialTemplate.id)}
                      onCopy={() => handleCopy(initialTemplate.id)}
                      onDelete={() => handleDelete(initialTemplate.id)}
                      categoryBorderRadius={categoryBorderRadius}
                      categoryBgColor={categoryBgColor}
                      dragEnabled={true}
                      titleOverride={currentTitle}
                      subheaderOverride={currentSubheader}
                      bodyOverride={currentBody}
                    />
                  );
                })()
              ) : (
                // No saved template was initially selected - show Current/Dummy config first
                currentOverlayConfig && (
                  <div
                    className={`relative bg-white rounded-xl shadow-sm border-2 transition-all duration-200 overflow-hidden
                      ${selectedTemplateId === CURRENT_CONFIG_ID ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}
                      ${isCurrentCardHovered ? 'shadow-md' : ''}`}
                    onMouseEnter={() => setIsCurrentCardHovered(true)}
                    onMouseLeave={() => setIsCurrentCardHovered(false)}
                  >
                    {/* Title - TOP (large, bold) */}
                    <div className="px-4 pt-4 pb-2">
                      <div className="text-xl font-bold text-gray-900">
                        Current Settings
                      </div>
                    </div>

                    {/* Image Preview - MIDDLE */}
                    <div className="px-4">
                      <div
                        className="relative cursor-pointer"
                        onClick={async () => {
                          setSelectedTemplateId(CURRENT_CONFIG_ID);
                          // Clear overlay_template_id since user is choosing custom settings
                          await tendApi.updateTendV2Content(contentItem.id, {
                            overlay_template_id: null,
                            overlay_config: currentConfigState,  // Save current config state
                          });
                          onSelectTemplate(CURRENT_CONFIG_ID, currentConfigState);
                        }}
                      >
                        {/* Badges on the image */}
                        <div className="absolute top-2 left-2 z-20 flex flex-col gap-1">
                          <span className="bg-purple-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                            CURRENT
                          </span>
                          {selectedTemplateId === CURRENT_CONFIG_ID && (
                            <span className="bg-blue-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                              SELECTED
                            </span>
                          )}
                        </div>
                        <OverlayPreview
                          contentItem={contentItem}
                          config={currentConfigState}
                          brandLogo={brandDefaultLogo}
                          brandName={brandName}
                          brandId={brandId}
                          onConfigChange={(updates) => setCurrentConfigState(prev => ({ ...prev, ...updates }))}
                          size="large"
                          controlsEnabled={true}
                          logoSelectable={true}
                          logoDisplayMode={brandLogoDisplayMode}
                          categoryBorderRadius={categoryBorderRadius}
                          categoryBgColor={categoryBgColor}
                          titleOverride={currentTitle}
                          subheaderOverride={currentSubheader}
                          bodyOverride={currentBody}
                        />

                        {/* Select overlay on hover (when not already selected) */}
                        {selectedTemplateId !== CURRENT_CONFIG_ID && isCurrentCardHovered && (
                          <div className="absolute inset-0 z-50 bg-blue-500/20 flex items-center justify-center pointer-events-none rounded-lg">
                            <span className="bg-blue-600 text-white text-sm font-medium px-3 py-1.5 rounded-full shadow-lg">
                              Click to Select
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Description and Actions - BOTTOM */}
                    <div className="p-4">
                      <div className="text-sm text-gray-600 leading-relaxed min-h-[60px]">
                        Unsaved overlay configuration. Edit the settings above, then save as a template to keep your changes.
                      </div>
                      {/* Save as Template button */}
                      <div className={`mt-3 pt-3 border-t transition-opacity ${isCurrentCardHovered ? 'opacity-100' : 'opacity-0'}`}>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            setIsCreating(true);
                            const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;
                            try {
                              const newTemplate = await tendApi.createOverlayTemplateV2(currentConfigState, {
                                brandId,
                                name: 'New Template',
                                description: 'Saved from current settings',
                              });
                              if (newTemplate) {
                                await loadTemplates();
                                setSelectedTemplateId(newTemplate.id);
                                onSelectTemplate(newTemplate.id, currentConfigState);
                                requestAnimationFrame(() => {
                                  if (scrollContainerRef.current) {
                                    scrollContainerRef.current.scrollTop = scrollTop;
                                  }
                                });
                              }
                            } catch (error) {
                              console.error('Failed to save template:', error);
                            } finally {
                              setIsCreating(false);
                            }
                          }}
                          disabled={isCreating}
                          className="w-full py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                          {isCreating ? 'Saving...' : 'Save as Template'}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* OTHER TEMPLATES: Show remaining templates (excluding the one shown first) */}
              {filteredTemplates
                .filter(t => t.id !== initialTemplateId) // Don't show initial template again (it's in first slot)
                .map((template) => {
                  const localState = localStates[template.id];
                  if (!localState) return null;

                  return (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      contentItem={contentItem}
                      brandName={brandName}
                      brandLogo={brandDefaultLogo}
                      brandId={brandId}
                      logoDisplayMode={brandLogoDisplayMode}
                      isSelected={selectedTemplateId === template.id}
                      isDefault={template.is_default ?? false}
                      isSuperAdmin={isSuperAdmin}
                      localConfig={localState.config}
                      localName={localState.name}
                      localDescription={localState.description}
                      isDirty={localState.isDirty}
                      onConfigChange={(updates) => handleConfigChange(template.id, updates)}
                      onNameChange={(name) => handleNameChange(template.id, name)}
                      onDescriptionChange={(desc) => handleDescriptionChange(template.id, desc)}
                      onSelect={() => handleSelect(template.id)}
                      onMakeDefault={() => handleMakeDefault(template.id)}
                      onSave={() => handleSave(template.id)}
                      onRevert={() => handleRevert(template.id)}
                      onCopy={() => handleCopy(template.id)}
                      onDelete={() => handleDelete(template.id)}
                      categoryBorderRadius={categoryBorderRadius}
                      categoryBgColor={categoryBgColor}
                      dragEnabled={true}
                      titleOverride={currentTitle}
                      subheaderOverride={currentSubheader}
                      bodyOverride={currentBody}
                    />
                  );
                })}

              {/* LAST SLOT: Create new template card - shows default template preview with PLUS overlay */}
              <div
                onClick={!isCreating ? handleCreateNew : undefined}
                className={`relative bg-white rounded-xl shadow-sm border-2 border-dashed transition-all duration-200 overflow-hidden cursor-pointer
                  ${isCreating ? 'opacity-50 cursor-not-allowed' : 'border-gray-300 hover:border-blue-400 hover:shadow-md'}`}
              >
                {/* Title - TOP (large, bold) */}
                <div className="px-4 pt-4 pb-2">
                  <div className="text-xl font-bold text-gray-600">
                    {isSuperAdmin && activeTab === 'global' ? 'New Global Template' : 'New Template'}
                  </div>
                </div>

                {/* Image Preview - MIDDLE */}
                <div className="px-4">
                  <div className="relative">
                    {/* Preview of default template config */}
                    <OverlayPreview
                      contentItem={contentItem}
                      config={effectiveDefaultConfig}
                      brandLogo={brandDefaultLogo}
                      brandName={brandName}
                      brandId={brandId}
                      onConfigChange={() => {}} // No-op since controls are disabled
                      size="large"
                      controlsEnabled={false}
                      categoryBorderRadius={categoryBorderRadius}
                      categoryBgColor={categoryBgColor}
                      titleOverride={currentTitle}
                      subheaderOverride={currentSubheader}
                      bodyOverride={currentBody}
                    />

                    {/* PLUS overlay on top of preview */}
                    <div className="absolute inset-0 z-50 bg-black/40 flex flex-col items-center justify-center rounded-lg">
                      {isCreating ? (
                        <div className="animate-spin rounded-full h-12 w-12 border-3 border-white border-t-transparent" />
                      ) : (
                        <>
                          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <span className="mt-3 text-white font-semibold text-sm drop-shadow-lg">
                            {isSuperAdmin && activeTab === 'global' ? 'Create Global Template' : 'Create Template'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description - BOTTOM */}
                <div className="p-4">
                  <div className="text-sm text-gray-500 leading-relaxed min-h-[60px]">
                    Click to create a new template starting from the default configuration.
                  </div>
                </div>
              </div>

              {/* Empty state - only show if no templates AND no current config */}
              {filteredTemplates.length === 0 && !currentOverlayConfig && !isLoading && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  <p className="text-lg">No templates found</p>
                  <p className="text-sm mt-1">Create your first template to get started</p>
                </div>
              )}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

      </div>
    </div>
    </BrandPaletteProvider>,
    document.body
  );
};

export default TemplateManagementModal;
